import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import {
  getGetVenuaPlannerCalendarTermsQueryKey,
  getGetVenuaPlannerTreeQueryKey,
  useGenerateVenuaPlannerTerms,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { Button, Input, Select, toast } from '@/shared/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn';
import { toCalendarGenerateRequest } from '../toCalendarGenerateRequest';

/** Série nabízená v selectu popoveru (zploštělý strom projekt → produkt → série). */
export interface CalendarSerieOption {
  productId: string;
  serieId: string;
  label: string;
  nights: number;
}

interface CreateTermsPopoverProps {
  /** ISO datum dne, ze kterého se popover otevřel (první předvyplněný začátek) */
  date: string;
  series: readonly CalendarSerieOption[];
}

/**
 * Popover „Nový termín z kalendáře“ — výběr série + seznam dat začátků,
 * submit volá existující bulk endpoint GenerateTerms v režimu Calendar.
 */
export function CreateTermsPopover({ date, series }: CreateTermsPopoverProps) {
  const { t, i18n } = useTranslation('planner');
  const queryClient = useQueryClient();
  const generateMutation = useGenerateVenuaPlannerTerms();

  const [open, setOpen] = useState(false);
  const [serieKey, setSerieKey] = useState('');
  const [dates, setDates] = useState<string[]>([date]);
  const [newDate, setNewDate] = useState('');

  const selectedSerie = series.find(
    (option) => `${option.productId}:${option.serieId}` === serieKey
  );
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  const reset = useCallback(() => {
    setSerieKey('');
    setDates([date]);
    setNewDate('');
  }, [date]);

  const handleSubmit = useCallback(async () => {
    if (!selectedSerie || dates.length === 0) return;
    try {
      const response = await generateMutation.mutateAsync({
        productId: selectedSerie.productId,
        serieId: selectedSerie.serieId,
        data: toCalendarGenerateRequest(dates),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerCalendarTermsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerTreeQueryKey() }),
      ]);
      toast({
        severity: 'success',
        description:
          t('calendar.create.toast.created', { count: response.createdCount }) +
          (response.skippedDuplicates > 0
            ? ` ${t('calendar.create.toast.skipped', { count: response.skippedDuplicates })}`
            : ''),
      });
      setOpen(false);
      reset();
    } catch {
      toast({
        severity: 'error',
        title: t('calendar.create.toast.errorTitle'),
        description: t('calendar.create.toast.error'),
      });
    }
  }, [selectedSerie, dates, generateMutation, queryClient, t, reset]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('calendar.create.openLabel')}
          data-testid="calendar-day-add"
          className="text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-text rounded-phx-sm flex h-4.5 w-4.5 items-center justify-center"
        >
          <Plus className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80" data-testid="calendar-create-popover">
        <div className="gap-phx-3 flex flex-col">
          <h4 className="text-phx-md text-phx-text font-semibold">{t('calendar.create.title')}</h4>

          <label className="gap-phx-1 flex flex-col">
            <span className="text-phx-xs text-phx-text-2 font-medium">
              {t('calendar.create.serie')}
            </span>
            <Select
              size="sm"
              value={serieKey}
              onChange={(event) => setSerieKey(event.target.value)}
              placeholder={t('calendar.create.seriePlaceholder')}
              options={series.map((option) => ({
                value: `${option.productId}:${option.serieId}`,
                label: option.label,
              }))}
              data-testid="calendar-create-serie"
            />
          </label>

          {selectedSerie && (
            <p className="text-phx-xs text-phx-text-muted">
              {t('calendar.create.nightsInfo', { count: selectedSerie.nights })}
            </p>
          )}

          <div className="gap-phx-1 flex flex-col">
            <span className="text-phx-xs text-phx-text-2 font-medium">
              {t('calendar.create.dates')}
            </span>
            <div className="gap-phx-2 flex flex-wrap">
              {[...dates].sort().map((value) => (
                <span
                  key={value}
                  className="bg-phx-surface-2 border-phx-border rounded-phx-sm text-phx-xs gap-phx-1 flex items-center border px-1.5 py-0.5"
                >
                  {dateFormat.format(new Date(`${value}T00:00:00`))}
                  {dates.length > 1 && (
                    <button
                      type="button"
                      aria-label={t('calendar.create.removeDate')}
                      onClick={() => setDates((prev) => prev.filter((d) => d !== value))}
                      className="text-phx-text-muted hover:text-phx-text"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            <div className="gap-phx-2 flex items-center">
              <Input
                type="date"
                size="sm"
                value={newDate}
                onChange={(event) => setNewDate(event.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!newDate || dates.includes(newDate)}
                onClick={() => {
                  setDates((prev) => [...prev, newDate]);
                  setNewDate('');
                }}
              >
                {t('calendar.create.addDate')}
              </Button>
            </div>
          </div>

          <div className="gap-phx-2 flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              {t('calendar.create.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!selectedSerie || dates.length === 0 || generateMutation.isPending}
              onClick={() => void handleSubmit()}
              data-testid="calendar-create-submit"
            >
              {t('calendar.create.submit', { count: dates.length })}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
